// Shared math for maintenance contracts. Used by:
//   - useOrgData.addContract / approveQuoteAndCreateContract — stamp the
//     correct per-visit price on each spawned visit job
//   - useOrgData.completeMaintenanceVisit — auto-draft the correct
//     per-visit bill line based on which year of the term the visit lands in
//   - ContractsPage / QuoteEditor / ContractDetailPage — preview math
//
// Annual escalator semantics (pre-QB runway #4):
//   - annualEscalationPct = 0 or blank — flat pricing across the whole
//     term (today's default behavior; unchanged for existing contracts).
//   - annualEscalationPct > 0 — year-N price = year-1 price × (1+pct/100)^(N-1).
//     The customer agrees to it at contract signing; the contractor offers
//     it to protect against wage / material / insurance inflation across
//     a multi-year term.

export const VISITS_PER_YEAR = {
  monthly: 12,
  quarterly: 4,
  biannual: 2,
  annual: 1,
};

export const visitsPerYearFor = (interval) =>
  VISITS_PER_YEAR[interval] || 4;

// Given a 1-indexed visit number and a contract's visit interval, returns
// which year of the term this visit lands in (1, 2, 3, ...). Example:
// quarterly contract, visit 5 → year 2.
export const yearOfTermForVisit = (visitIndex, visitInterval) => {
  const vpy = visitsPerYearFor(visitInterval);
  return Math.floor((Math.max(1, visitIndex) - 1) / vpy) + 1;
};

// Per-visit customer price for one specific visit on a contract,
// applying any annual escalator from year 2 onward. Accepts either a
// real contract doc or a quote-shaped object (uses the fields it needs).
export const perVisitPriceForVisit = (contract, visitIndex) => {
  const vpy = visitsPerYearFor(contract?.visitInterval);
  if (!vpy) return 0;
  const annual = Number(contract?.annualPrice) || 0;
  const basePerVisit = annual / vpy;
  const pct = Number(contract?.annualEscalationPct) || 0;
  if (pct === 0) return basePerVisit;
  const year = yearOfTermForVisit(visitIndex, contract?.visitInterval);
  return basePerVisit * Math.pow(1 + pct / 100, year - 1);
};

// Returns an array of { year, annualPrice, perVisitPrice } projecting
// escalated pricing across the full term. Used by preview blocks.
export const projectedAnnualPrices = (contract) => {
  const termYears = Number(contract?.termYears) || 1;
  const vpy = visitsPerYearFor(contract?.visitInterval);
  const baseAnnual = Number(contract?.annualPrice) || 0;
  const pct = Number(contract?.annualEscalationPct) || 0;
  const out = [];
  for (let y = 1; y <= termYears; y++) {
    const annual = baseAnnual * Math.pow(1 + pct / 100, y - 1);
    out.push({
      year: y,
      annualPrice: annual,
      perVisitPrice: vpy > 0 ? annual / vpy : 0,
    });
  }
  return out;
};

// Sum of all years' annual prices across the term — the true lifetime
// contract value when an escalator is applied (otherwise just
// annualPrice × termYears).
export const lifetimeContractValue = (contract) =>
  projectedAnnualPrices(contract).reduce((s, y) => s + y.annualPrice, 0);
